import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { LanguageProvider } from "./i18n/LanguageContext";
import { CartProvider } from "./context/CartContext";
import { PortalProvider } from "./context/PortalContext";
import { AuthProvider } from "./context/AuthContext";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";
import ExitIntent from "./components/ExitIntent";
import VisitTracker from "./components/VisitTracker";
import Concierge from "./components/Concierge";
import CursorGlow from "./components/CursorGlow";
import AuthCallback from "./components/AuthCallback";
import { Toaster } from "./components/ui/sonner";

const Home = lazy(() => import("./pages/Home"));
const Shop = lazy(() => import("./pages/Shop"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Story = lazy(() => import("./pages/Story"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Account = lazy(() => import("./pages/Account"));
const Admin = lazy(() => import("./pages/Admin"));
const Cupprint = lazy(() => import("./pages/Cupprint"));
const BrewRitual = lazy(() => import("./pages/BrewRitual"));
const Policies = lazy(() => import("./pages/Policies"));

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  // Detect OAuth session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/shop" element={<Shop />} />
      <Route path="/product/:handle" element={<ProductDetail />} />
      <Route path="/story" element={<Story />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/account" element={<Account />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/cupprint" element={<Cupprint />} />
      <Route path="/brew/:handle" element={<BrewRitual />} />
      <Route path="/policies" element={<Policies />} />
    </Routes>
  );
}

function Shell() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="grain min-h-screen bg-xir-dark text-xir-paper">
      <ScrollManager />
      <Nav />
      <CartDrawer />
      <Suspense fallback={<div className="min-h-screen bg-xir-dark" />}>
        <AppRoutes />
      </Suspense>
      <Footer />
      <ExitIntent />
      <VisitTracker />
      <Concierge />
      <CursorGlow />
      <Toaster theme="dark" position="bottom-center" />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <CartProvider>
            <PortalProvider>
              <Shell />
            </PortalProvider>
          </CartProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}
